<template>
  <div>
    <div class="cm-drill-backdrop" @click="close"></div>

    <aside class="cm-drill">
      <span class="close-main" @click="close">
        <i class="bi bi-x-lg"></i>
      </span>
      <div class="cm-drill-head">
        <div>
          <p class="cm-hint">{{ $t('CM_COL_TERM') }}</p>
          <!-- набор слов: кликнутое слово + добавленные вручную (AND) -->
          <div class="cm-drill-terms">
            <span class="cm-drill-term">
              <i v-if="format === 'media'" class="bi bi-image" :title="$t('CM_CHIP_IMAGE')"></i>
              {{ term }}
            </span>

            <span
              v-for="node in comboNodes"
              :key="node.key"
              :class="chipNodeClass(node)"
              :aria-hidden="node.kind === 'sep' ? 'true' : null"
              @mouseenter="onChipEnter(node)"
              @mouseleave="onChipLeave(node)"
              @click="node.kind === 'chip' && removeTerm(node.i)"
            >
              <i v-if="node.kind === 'sep'" class="bi bi-plus-lg"></i>
              <template v-else>{{ node.t }}</template>
            </span>

            <template v-if="canAddTerm">
              <button
                class="cm-combo-add"
                :class="{ 'remove-term': hoverRemoveIndex === extra.length - 1 }"
                :title="$t('CM_COMBO_ADD')"
                @mousedown="onAddMouseDown"
                @click="toggleAdd"
                @mouseenter="hoverAdd = true"
                @mouseleave="hoverAdd = false"
              >
                <svg v-if="!adding" class="cm-combo-add__border" aria-hidden="true">
                  <circle cx="50%" cy="50%" r="10"></circle>
                </svg>
                <i :class="adding && hoverAdd ? 'bi bi-dash-lg' : 'bi bi-plus-lg'"></i>
              </button>
              <input
                v-if="adding"
                ref="comboInput"
                v-model="draft"
                class="cm-combo-input"
                type="text"
                :placeholder="$t('CM_COMBO_PLACEHOLDER')"
                @keyup.enter="addTerm"
                @keyup.esc="cancelAdd"
                @blur="cancelAdd"
              />
              <span v-else-if="hoverAdd" class="cm-combo-input-dots">...</span>
            </template>
          </div>

          <!-- сколько постов реально попало в срез: у набора счёт падает быстро -->
          <p v-if="isCombo && data" class="cm-hint cm-combo-count">
            {{ $t('CM_COMBO_POSTS') }}: <b>{{ data.postsCount }}</b>
          </p>
        </div>
      </div>

      <div class="cm-drill-actions">
        <button v-if="!isCombo" class="btn-st-tr btn-track" @click="track" :class="isTracked ? 'btn-tracked' : null" :title="isTracked ? $t('CM_UNTRACK') : $t('CM_TRACK')">
          ★ {{ isTracked ? $t('CM_UNTRACK') : $t('CM_TRACK') }}
        </button>
        <button v-if="!isCombo" class="btn-st-tr btn-exclude" @click="exclude">{{ $t('CM_EXCLUDE') }}</button>
        <button class="btn-st-tr" @click="filterFeed">{{ $t('CM_FILTER_FEED') }}</button>
      </div>

      <div v-if="loading" class="cm-state">{{ $t('CM_LOADING') }}</div>
      <div v-else-if="error" class="cm-state is-error">{{ error }}</div>
      <!-- набор без пересечений: чипы выше остаются, чтобы слово можно было снять -->
      <div v-else-if="isCombo && data && !data.postsCount" class="cm-state">
        {{ $t('CM_COMBO_EMPTY') }}
      </div>

      <template v-else-if="data">
        <p v-if="lowData" class="cm-combo-warn">{{ $t('CM_COMBO_LOW_DATA') }}</p>

        <div class="cm-drill-cards">
          <div class="panel">
            <div class="cm-card-value">{{ formatNum(data.postsCount) }}</div>
            <div class="cm-card-label">{{ $t('CM_DRILL_POST_COUNT') }}</div>
          </div>
          <div class="panel">
            <div class="cm-card-value">{{ formatAverage(averageRelativeActivity) }}</div>
            <div class="cm-card-label">{{ $t('CM_DRILL_AVG_REL_ACTIVITY') }}</div>
          </div>
          <div class="panel">
            <div class="cm-card-value">{{ formatAverage(averageRelativeDiscussion) }}</div>
            <div class="cm-card-label">{{ $t('CM_DRILL_AVG_REL_DISCUSSION') }}</div>
          </div>
        </div>

        <Cm-term-timeline :series="data.series" />

        <Cm-term-rhythm :by-weekday="data.byWeekday" :by-hour="data.byHour" :tz="data.tz" />

        <template v-if="showAuthorSections">
          <template v-if="sortedWeight.length">
            <div class="cm-chart-title">{{ $t('CM_DRILL_WEIGHT') }}</div>
            <div v-if="showAuthorPies" ref="weightChart" class="cm-chart-sm"></div>
            <table class="cm-table">
              <thead>
                <tr>
                  <th
                    :class="classTableHead('weight', 'page_name')"
                    @click="toggleSort('weight', 'page_name')"
                  >{{ $t('CM_COL_PAGE') }}</th>
                  <th
                    :class="['is-num', classTableHead('weight', 'posts_count')]"
                    @click="toggleSort('weight', 'posts_count')"
                  >{{ $t('CM_COL_POSTS') }}</th>
                  <th
                    :class="classTableHead('weight', 'contribution_pct')"
                    @click="toggleSort('weight', 'contribution_pct')"
                  >{{ $t('CM_DRILL_CONTRIB') }}</th>
                </tr>
              </thead>
              <tbody>
                <tr
                  v-for="row in sortedWeight"
                  :key="row.page_name"
                  @mouseenter="highlightRow('weight', row.page_name)"
                  @mouseleave="downplayRow('weight', row.page_name)"
                >
                  <td :title="row.page_name">
                    <div class="drill-page-name">
                      <span class="cm-dot" :style="{ background: colorOfPage(row.page_name) }"></span>
                      <span v-if="row.social" :class="`soc-ico soc-${row.social.toLowerCase()}`"></span>
                      {{ truncate(row.page_name) }}
                      <a v-if="row.link" :href="row.link" target="_blank" rel="noopener noreferrer" @click.stop>
                        <i class="bi bi-link-45deg"></i>
                      </a>
                    </div>
                  </td>
                  <td class="is-num">{{ row.posts_count }}</td>
                  <td>
                    <span class="cm-bar">
                      <span class="cm-bar-track">
                        <span class="cm-bar-fill cm-tone-pink" :style="{ width: `${row.contribution_pct}%` }"></span>
                      </span>
                      <span class="cm-bar-val">{{ row.contribution_pct }}%</span>
                    </span>
                  </td>
                </tr>
              </tbody>
            </table>
          </template>

          <template v-if="sortedShare.length">
            <div class="cm-chart-title">{{ $t('CM_DRILL_SHARE') }}</div>
            <div v-if="showAuthorPies" ref="shareChart" class="cm-chart-sm"></div>
            <table class="cm-table">
              <thead>
                <tr>
                  <th
                    :class="classTableHead('share', 'page_name')"
                    @click="toggleSort('share', 'page_name')"
                  >{{ $t('CM_COL_PAGE') }}</th>
                  <th
                    :class="classTableHead('share', 'mention_share_pct')"
                    @click="toggleSort('share', 'mention_share_pct')"
                  >{{ $t('CM_DRILL_MENTION_SHARE') }}</th>
                  <th
                    :class="['is-num', classTableHead('share', 'content_share_pct')]"
                    @click="toggleSort('share', 'content_share_pct')"
                  >{{ $t('CM_DRILL_CONTENT_SHARE') }}</th>
                </tr>
              </thead>
              <tbody>
                <tr
                  v-for="row in sortedShare"
                  :key="row.page_name"
                  @mouseenter="highlightRow('share', row.page_name)"
                  @mouseleave="downplayRow('share', row.page_name)"
                >
                  <td :title="row.page_name">
                    <div class="drill-page-name">
                      <span class="cm-dot" :style="{ background: colorOfPage(row.page_name) }"></span>
                      <span v-if="row.social" :class="`soc-ico soc-${row.social.toLowerCase()}`"></span>
                      {{ truncate(row.page_name) }}
                      <a v-if="row.link" :href="row.link" target="_blank" rel="noopener noreferrer" @click.stop>
                        <i class="bi bi-link-45deg"></i>
                      </a>
                    </div>
                  </td>
                  <td>
                    <span class="cm-bar">
                      <span class="cm-bar-track">
                        <span
                          class="cm-bar-fill cm-tone-amber"
                          :style="{ width: `${row.mention_share_pct}%` }"
                        ></span>
                      </span>
                      <span class="cm-bar-val">{{ row.mention_share_pct }}%</span>
                    </span>
                  </td>
                  <td class="is-num">{{ row.content_share_pct }}%</td>
                </tr>
              </tbody>
            </table>
          </template>
        </template>

        <div class="cm-feed cm-drill-posts">
          <div class="cm-feed-head">
            <h2>{{ $t('CM_DRILL_POSTS_CONTAINING') }} <span class="cm-term-text">{{ allTerms.join(' + ') }}</span></h2>
            <button class="btn-st-tr" @click="filterFeed">{{ $t('CM_DRILL_SEE_ALL') }}</button>
          </div>
          <div v-if="previewLoading" class="cm-state">{{ $t('CM_LOADING') }}</div>
          <div v-else-if="previewError" class="cm-state is-error">{{ previewError }}</div>
          <template v-else>
            <div class="one-page">
              <div class="panel">
                <ul v-if="previewItems.length" class="posts">
                  <Cm-feed-one
                    v-for="post in previewItems"
                    :key="post.id"
                    :post="post"
                    :page="pageOf(post)"
                    layout="list"
                  />
                </ul>
                <p v-else class="no-posts">{{ $t('NO_POSTS') }}</p>
              </div>
            </div>
            <button v-if="previewItems.length" class="btn-st-tr cm-drill-see-all" @click="filterFeed">
              {{ $t('CM_DRILL_SEE_ALL') }}
            </button>
          </template>
        </div>
      </template>
    </aside>
  </div>
</template>

<script>
import * as echarts from "echarts";
import { debounce } from "lodash";
import { mapGetters, mapActions } from "vuex";
import cmApi from "@/api/controllers/content-monitoring";
import bus from "@/models/content-monitoring/bus";
import { CM_ADD_WORD, CM_REMOVE_WORD } from "@/store/actions/contentMonitoring";
import { enableTouchTooltip } from "@/models/charts/echarts-touch";
import cmTableSortMixin, { createSortState } from "@/mixins/cmTableSortMixin";
import CmTermRhythm from "./Cm-term-rhythm";
import CmTermTimeline from "./Cm-term-timeline";
import CmFeedOne from "./Cm-feed-one";
import pageUtils from "@/models/page-utils";

const COMBO_EXTRA_MAX = 2;
const LOW_DATA_POSTS = 10;

export default {
  name: "Cm-drilldown",
  components: { CmTermRhythm, CmTermTimeline, CmFeedOne },
  mixins: [cmTableSortMixin],

  props: {
    term: { type: String, required: true },
    scope: { type: Object, required: true },
    // 'media' — слово пришло с картинки (чип в ленте): считаем по картинкам,
    // а не по вкладке; '' — как вкладка
    format: { type: String, default: "" },
    // term — аккаунт/ссылка из «Упоминаний»: сервер ищет его как упоминание, а не слово
    entity: { type: Boolean, default: false },
  },

  data() {
    return {
      data: null,
      loading: false,
      error: "",
      previewItems: [],
      previewLoading: false,
      previewError: "",
      extra: [],
      adding: false,
      hoverAdd: false,
      hoverRemoveIndex: null,
      draft: "",
      sorts: {
        weight: createSortState(
          ["page_name", "posts_count", "contribution_pct"],
          "page_name",
        ),
        share: createSortState(
          ["page_name", "mention_share_pct", "content_share_pct"],
          "page_name",
        ),
      },
    };
  },

  computed: {
    ...mapGetters(["getPeriod", "getCmFormat", "getCmAdded"]),

    isTracked() {
      return this.getCmAdded.includes(this.term);
    },
    allTerms() {
      return [this.term, ...this.extra];
    },
    isCombo() {
      return this.extra.length > 0;
    },

    /** Flat sep/chip nodes so each has its own key (Vue 2 <template v-for>). */
    comboNodes() {
      const nodes = [];
      this.extra.forEach((t, i) => {
        nodes.push({ kind: "sep", key: `sep-${i}` });
        nodes.push({ kind: "chip", key: `chip-${i}`, t, i });
      });
      return nodes;
    },

    canAddTerm() {
      return this.extra.length < COMBO_EXTRA_MAX;
    },
    lowData() {
      if (!this.isCombo || !this.data) return false;
      const n = this.data.postsCount;
      return n > 0 && n < LOW_DATA_POSTS;
    },
    isMultiPageScope() {
      return ((this.scope && this.scope.pageIds && this.scope.pageIds.length) || 0) > 1;
    },
    showAuthorSections() {
      if (!this.isMultiPageScope || !this.data) return false;
      return !!(
        (this.data.weight && this.data.weight.length) ||
        (this.data.share && this.data.share.length)
      );
    },
    showAuthorPies() {
      if (!this.showAuthorSections) return false;
      const weightLen = (this.data.weight && this.data.weight.length) || 0;
      const shareLen = (this.data.share && this.data.share.length) || 0;
      return Math.max(weightLen, shareLen) > 1;
    },

    sortedWeight() {
      return this.data
        ? this.sortRows(this.data.weight, "weight", { stringFields: ["page_name"] })
        : [];
    },
    sortedShare() {
      return this.data
        ? this.sortRows(this.data.share, "share", { stringFields: ["page_name"] })
        : [];
    },

    // Средние за период берём у сервера: он считает их по постам, как колонки
    // топов. Усреднение дневных точек ряда давало другую цифру у частых слов
    // (день с одним постом весил как день с тридцатью) — оставлено запасным
    // путём для ответа без этих полей.
    averageRelativeActivity() {
      const v = this.data && this.data.avgRelativeEr;
      return v === undefined ? this.averageOf("rel_er") : v;
    },

    averageRelativeDiscussion() {
      const v = this.data && this.data.avgRelativeTr;
      return v === undefined ? this.averageOf("rel_tr") : v;
    },

    palette() {
      const c = this.$colors;
      return [c.blue, c.viol, c.red, c.green, c.orange, c.pink, c.yellow];
    },
    pageColorMap() {
      const map = {};
      if (!this.data) return map;
      let i = 0;
      const add = (rows) => {
        (rows || []).forEach((row) => {
          if (!(row.page_name in map)) {
            map[row.page_name] = this.palette[i % this.palette.length];
            i += 1;
          }
        });
      };
      add(this.data.weight);
      add(this.data.share);
      return map;
    },
  },

  watch: {
    term() {
      this.extra = [];
      this.adding = false;
      this.hoverAdd = false;
      this.hoverRemoveIndex = null;
      this.draft = "";
      this.load();
    },
  },

  methods: {
    ...mapActions([CM_ADD_WORD, CM_REMOVE_WORD]),

    chipNodeClass(node) {
      if (node.kind === "chip") return "cm-combo-chip";
      // sep after hovered chip (next .cm-combo-add in the row)
      return [
        "cm-combo-add",
        this.hoverRemoveIndex != null && this.hoverRemoveIndex === node.i - 1
          ? "remove-term"
          : null,
      ];
    },

    onChipEnter(node) {
      if (node.kind === "chip") this.hoverRemoveIndex = node.i;
    },

    onChipLeave(node) {
      if (node.kind === "chip" && this.hoverRemoveIndex === node.i) {
        this.hoverRemoveIndex = null;
      }
    },

    truncate(value, max = 20) {
      const str = (value || "").toString();
      return str.length > max ? `${str.slice(0, max)}…` : str;
    },

    formatNum(value) {
      return pageUtils.formatNumbers(value);
    },

    averageOf(field) {
      const values = ((this.data && this.data.series) || [])
        .map((point) => point[field])
        .filter((value) => value != null && Number.isFinite(Number(value)))
        .map(Number);
      if (!values.length) return null;
      return values.reduce((sum, value) => sum + value, 0) / values.length;
    },

    formatAverage(value) {
      return value == null ? "—" : `${Math.round(value * 100) / 100}%`;
    },

    pageOf(post) {
      const pages = this.scope && this.scope.pageById;
      return (pages && pages.get(Number(post.page_id))) || {};
    },

    colorOfPage(pageName) {
      return this.pageColorMap[pageName] || this.palette[0];
    },

    chartIndexOf(key, pageName) {
      const rows = key === "weight" ? this.data.weight : this.data.share;
      return (rows || []).findIndex((row) => row.page_name === pageName);
    },

    close() {
      this.$emit("close");
    },

    track() {
      if (this.isTracked) this.CM_REMOVE_WORD({ list: "added", word: this.term });
      else this.CM_ADD_WORD({ list: "added", word: this.term });
    },

    exclude() {
      this.CM_ADD_WORD({ list: "excluded", word: this.term });
      this.close();
    },

    /** Prevent input blur→reopen race when clicking "-" to close. */
    onAddMouseDown(e) {
      if (this.adding) e.preventDefault();
    },

    toggleAdd() {
      if (this.adding) {
        this.cancelAdd();
        return;
      }
      if (!this.canAddTerm) return;
      this.adding = true;
      this.draft = "";
      this.$nextTick(() => {
        const el = this.$refs.comboInput;
        if (el) el.focus();
      });
    },

    cancelAdd() {
      this.adding = false;
      this.draft = "";
    },

    addTerm() {
      const word = (this.draft || "").trim();
      this.adding = false;
      this.draft = "";
      if (!word || !this.canAddTerm) return;
      if (this.allTerms.some((t) => t.toLowerCase() === word.toLowerCase())) return;
      this.extra = [...this.extra, word];
      this.load();
    },

    removeTerm(index) {
      this.extra = this.extra.filter((_, i) => i !== index);
      this.load();
    },

    filterFeed() {
      const terms = this.allTerms;
      this.close();
      this.$nextTick(() => {
        bus.$emit("cm:filter-term", terms, this.format || "");
      });
    },
    buildDualPieSeries(rows, outerField, innerField, outerName, innerName) {
      const makeData = (field) =>
        (rows || []).map((row) => ({
          name: row.page_name,
          value: Number(row[field]) || 0,
          itemStyle: {
            color: this.colorOfPage(row.page_name),
            borderRadius: 10,
            borderColor: "#fff",
            borderWidth: 2,
          },
        }));

      const base = {
        type: "pie",
        center: ["50%", "50%"],
        startAngle: 90,
        avoidLabelOverlap: true,
        label: { show: false },
        labelLine: { show: false },
        emphasis: {
          scale: true,
          scaleSize: 1.1,
          itemStyle: { borderWidth: 1 },
        },
      };

      return [
        {
          ...base,
          name: outerName,
          radius: ["82%", "90%"],
          data: makeData(outerField),
        },
        {
          ...base,
          name: innerName,
          radius: ["66%", "74%"],
          data: makeData(innerField),
        },
      ];
    },

    tooltipFormatter(key, cfg, params) {
      const rows = key === "weight" ? this.data.weight : this.data.share;
      const row = rows && rows[params.dataIndex];
      if (!row) return "";

      const color = this.colorOfPage(row.page_name);
      const title = this.truncate(row.page_name, 28);
      const outerVal = cfg.outerIsPct ? `${row[cfg.outerField]}%` : row[cfg.outerField];
      const innerVal = cfg.innerIsPct ? `${row[cfg.innerField]}%` : row[cfg.innerField];

      return `<div class="tooltip">
        <span class="date" style="color:${color};opacity:1">${title}</span>
        <ul class="tooltip-list">
          <li data-value="${row[cfg.outerField]}">
            <div class="soc-sea">${cfg.outerName}</div>${outerVal}
          </li>
          <li data-value="${row[cfg.innerField]}">
            <div class="soc-sea">${cfg.innerName}</div>${innerVal}
          </li>
        </ul>
      </div>`;
    },

    centerFormatter(key, dataIndex) {
      if (!this.data) return "";
      if (key === "weight") {
        const row = this.data.weight[dataIndex];
        if (!row) return "";
        return `${row.posts_count}\n${row.contribution_pct}%`;
      }
      const row = this.data.share[dataIndex];
      if (!row) return "";
      return `${row.mention_share_pct}%\n${row.content_share_pct}%`;
    },

    setCenterLabel(key, dataIndex) {
      const chart = this.chartRefs[key];
      if (!chart) return;
      chart.setOption({
        graphic: [
          {
            id: "cmDrillCenter",
            type: "text",
            left: "center",
            top: "middle",
            silent: true,
            z: 100,
            style: {
              text: this.centerFormatter(key, dataIndex),
              textAlign: "center",
              textVerticalAlign: "middle",
              fill: "#222",
              fontSize: 12,
              fontWeight: 600,
              lineHeight: 16,
            },
          },
        ],
      });
    },

    clearCenterLabel(key) {
      const chart = this.chartRefs[key];
      if (!chart) return;
      chart.setOption({
        graphic: [
          {
            id: "cmDrillCenter",
            type: "text",
            style: { text: "" },
          },
        ],
      });
    },

    renderChart(key) {
      if (!this.data) return;

      const configs = {
        weight: {
          rows: this.data.weight,
          outerField: "posts_count",
          innerField: "contribution_pct",
          outerName: this.$t("CM_COL_POSTS"),
          innerName: this.$t("CM_DRILL_CONTRIB"),
          outerIsPct: false,
          innerIsPct: true,
        },
        share: {
          rows: this.data.share,
          outerField: "mention_share_pct",
          innerField: "content_share_pct",
          outerName: this.$t("CM_DRILL_MENTION_SHARE"),
          innerName: this.$t("CM_DRILL_CONTENT_SHARE"),
          outerIsPct: true,
          innerIsPct: true,
        },
      };

      const cfg = configs[key];
      if (!cfg || !cfg.rows || !cfg.rows.length) {
        this.disposeChart(key);
        return;
      }

      const refName = key === "weight" ? "weightChart" : "shareChart";
      const holder = this.$refs[refName];
      const el = Array.isArray(holder) ? holder[0] : holder;
      if (!el) return;

      let chart = this.chartRefs[key];
      if (!chart) {
        chart = echarts.init(el, null, { renderer: "canvas", useDirtyRect: false });
        enableTouchTooltip(chart);
        this.chartRefs[key] = chart;
      }

      chart.setOption(
        {
          legend: { show: false },
          graphic: [
            {
              id: "cmDrillCenter",
              type: "text",
              left: "center",
              top: "middle",
              silent: true,
              z: 100,
              style: {
                text: "",
                textAlign: "center",
                textVerticalAlign: "middle",
                fill: "#222",
                fontSize: 12,
                fontWeight: 600,
                lineHeight: 16,
              },
            },
          ],
          tooltip: {
            trigger: "item",
            triggerOn: "mousemove|click",
            confine: true,
            backgroundColor: "transparent",
            borderWidth: 0,
            padding: 0,
            transitionDuration: 0.4,
            formatter: (params) => this.tooltipFormatter(key, cfg, params),
          },
          series: this.buildDualPieSeries(
            cfg.rows,
            cfg.outerField,
            cfg.innerField,
            cfg.outerName,
            cfg.innerName,
          ),
        },
        { notMerge: true },
      );
      chart.off("mouseover");
      chart.off("globalout");
      chart.on("mouseover", { seriesType: "pie" }, (params) => {
        if (params.dataIndex == null) return;
        this.highlightSlices(key, params.dataIndex);
      });
      chart.on("globalout", () => {
        this.downplaySlices(key);
      });

      chart.resize();
    },

    renderCharts() {
      if (!this.showAuthorPies) {
        this.disposeCharts();
        return;
      }
      this.renderChart("weight");
      this.renderChart("share");
    },

    highlightSlices(key, dataIndex) {
      const chart = this.chartRefs[key];
      if (!chart || dataIndex < 0) return;
      if (this._hlIndex[key] === dataIndex) return;
      this._hlIndex[key] = dataIndex;

      chart.dispatchAction({ type: "downplay", seriesIndex: 0 });
      chart.dispatchAction({ type: "downplay", seriesIndex: 1 });
      chart.dispatchAction({ type: "highlight", seriesIndex: 0, dataIndex });
      chart.dispatchAction({ type: "highlight", seriesIndex: 1, dataIndex });
      this.setCenterLabel(key, dataIndex);
    },

    downplaySlices(key, dataIndex) {
      const chart = this.chartRefs[key];
      if (!chart) return;
      if (this._hlIndex[key] == null && (dataIndex == null || dataIndex < 0)) return;
      this._hlIndex[key] = null;

      if (dataIndex == null || dataIndex < 0) {
        chart.dispatchAction({ type: "downplay", seriesIndex: 0 });
        chart.dispatchAction({ type: "downplay", seriesIndex: 1 });
      } else {
        chart.dispatchAction({ type: "downplay", seriesIndex: 0, dataIndex });
        chart.dispatchAction({ type: "downplay", seriesIndex: 1, dataIndex });
      }
      this.clearCenterLabel(key);
    },
    highlightRow(key, pageName) {
      this.highlightSlices(key, this.chartIndexOf(key, pageName));
    },

    downplayRow(key, pageName) {
      this.downplaySlices(key, this.chartIndexOf(key, pageName));
    },

    disposeChart(key) {
      const chart = this.chartRefs[key];
      if (chart) {
        chart.dispose();
        delete this.chartRefs[key];
      }
      if (this._hlIndex) this._hlIndex[key] = null;
    },

    disposeCharts() {
      Object.keys(this.chartRefs).forEach((key) => this.disposeChart(key));
      this.chartRefs = {};
      this._hlIndex = {};
    },

    resizeCharts() {
      // только resize: полный renderCharts на каждое окно — лишний CPU
      Object.keys(this.chartRefs).forEach((key) => {
        const chart = this.chartRefs[key];
        if (chart) chart.resize();
      });
    },

    loadPreview(id) {
      if (!this.data || !this.data.postsCount) return Promise.resolve();
      this.previewLoading = true;
      this.previewError = "";
      return cmApi
        .fetchFeed(
          {
            period: this.getPeriod,
            format: this.format || this.getCmFormat,
            // искать слова там же, где их нашёл drill-down (чип с текста / с картинки)
            termSource: this.format || undefined,
            term: this.allTerms,
            entity: this.entity ? this.term : undefined,
            page: 1,
            perPage: 3,
            sort: "activity",
            order: "desc",
          },
          this.scope,
        )
        .then((result) => {
          if (id !== this.requestId) return;
          this.previewItems = (result && result.items) || [];
        })
        .catch((e) => {
          if (id !== this.requestId) return;
          this.previewError = e.message;
        })
        .finally(() => {
          if (id === this.requestId) this.previewLoading = false;
        });
    },

    load() {
      const id = (this.requestId += 1);
      const isStale = () => id !== this.requestId;

      this.disposeCharts();
      this.loading = true;
      this.error = "";
      this.previewItems = [];
      this.previewLoading = false;
      this.previewError = "";

      cmApi
        .fetchTerm(
          {
            period: this.getPeriod,
            format: this.format || this.getCmFormat,
            term: this.term,
            extra: this.extra,
            entity: this.entity ? this.term : undefined,
          },
          this.scope,
        )
        .then((data) => {
          if (isStale()) return;
          this.data = data;
          if (data.terms && data.terms.length > 1) {
            this.extra = data.terms.slice(1, 1 + COMBO_EXTRA_MAX);
          }
          this.loadPreview(id);
        })
        .catch((e) => {
          if (isStale()) return;
          this.error = e.message;
        })
        .finally(() => {
          if (isStale()) return;
          this.loading = false;
          this.$nextTick(() => this.renderCharts());
        });
    },
  },

  created() {
    // echarts НЕ в data: Vue 2 сделал бы инстансы реактивными → постоянный CPU
    this.requestId = 0;
    this.chartRefs = {};
    this._hlIndex = {};
    this.onResize = debounce(this.resizeCharts, 150);
  },

  mounted() {
    window.addEventListener("resize", this.onResize);
    this.load();
  },

  beforeDestroy() {
    window.removeEventListener("resize", this.onResize);
    if (this.onResize && this.onResize.cancel) this.onResize.cancel();
    this.disposeCharts();
  },
};
</script>

<style scoped>

.cm-combo-chip {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 2px 8px;
  font-size: 14px;
  font-weight: 600;
  color: #2196f3;
  background: rgba(33, 150, 243, 0.1);
  border-radius: 4px;
}



.cm-combo-count {
  margin-top: 6px;
}
.cm-combo-warn {
  margin: 0 0 8px;
  padding: 6px 10px;
  font-size: 12px;
  color: #8a6d3b;
  background: #fcf8e3;
  border-radius: 4px;
}
</style>
